import React, { useState, useCallback, useMemo, useEffect, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import type { DropResult } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid';
import { Button, Flex, Input, Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { VisitorCustomFieldType } from 'graphql-query-contracts';
import Dropdown from 'components/form/Dropdown';
import Label from 'components/general/Label';
import { CustomFieldRenderer } from '../../../../feature/components/CustomFieldsSection/CustomFieldRenderer';
import type {
  CustomFieldFormData,
  CustomFieldOption,
  CustomFieldOptionRow,
} from '../../../../feature/components/CustomFieldsSection/customField.types';
import {
  VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS,
  coerceVisitorCustomFieldTypeFromUi,
  getVisitorCustomFieldTypeLabelKey,
  isVisitorCustomFieldPlaceholderSupported,
  isVisitorCustomFieldSelectType,
} from '../../../../feature/components/CustomFieldsSection/customField.constants';

import { CustomFieldModalOptionRowsEditor } from './CustomFieldModalOptionRowsEditor';
import {
  CUSTOM_FIELD_LABEL_MAX_LENGTH,
  CUSTOM_FIELD_SELECT_MAX_OPTIONS,
  CUSTOM_FIELD_SELECT_OPTION_TEXT_MAX_LENGTH,
  CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH,
} from '../../../../customFieldLimits';
import styles from './CustomFieldModal.scss';

const DEFAULT_FIELD_TYPE = VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS[0].value;
const DEFAULT_OPTION_COUNT = 3;

function createDefaultOptionRows(): CustomFieldOptionRow[] {
  return Array.from({ length: DEFAULT_OPTION_COUNT }, (_, i) => {
    const num = i + 1;

    return { id: uuid(), displayText: `Option ${num}`, value: `option_${num}` };
  });
}

export type CustomFieldModalProps = {
  onClose: () => void;
  /**
   * true  → field persisted, modal closes.
   * null  → caller blocked the save (e.g. limit exceeded) and already showed
   *          a toast — modal stays open with no additional error message.
   * false → save was attempted but failed — modal shows the generic
   *          "Couldn't save this field" inline error.
   */
  onSave: (data: CustomFieldFormData) => Promise<boolean | null>;
  onUnsavedDraftChange?: (hasUnsavedDraft: boolean) => void;
  /** Disables the Create button externally — e.g. while prerequisite data is still loading. */
  saveDisabled?: boolean;
};

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

type ValidateFormParams = {
  fieldName: string;
  fieldKey: string;
  fieldType: VisitorCustomFieldType;
  fieldTypeLabel: string;
  optionRows: CustomFieldOptionRow[];
  placeholder: string;
  t: TranslateFn;
};

type UseCustomFieldFormParams = {
  onClose: () => void;
  onSave: (data: CustomFieldFormData) => Promise<boolean | null>;
  t: TranslateFn;
};

const REQUIRED_ASTERISK = '*';

function deriveFieldKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+(\w)/g, (_, char) => char.toUpperCase());
}

function getOptionsForSave(
  fieldType: VisitorCustomFieldType,
  optionRows: CustomFieldOptionRow[]
): CustomFieldOption[] {
  if (!isVisitorCustomFieldSelectType(fieldType)) return [];

  return optionRows
    .filter(r => r.displayText.trim() !== '' && r.value.trim() !== '')
    .map(r => ({ displayText: r.displayText.trim(), value: r.value.trim() }));
}

type SaveUiState = { saving: boolean; submitError: string };

type RunCustomFieldSaveParams = {
  validate: () => boolean;
  setPersistUi: Dispatch<SetStateAction<SaveUiState>>;
  fieldType: VisitorCustomFieldType;
  optionRows: CustomFieldOptionRow[];
  fieldName: string;
  fieldKey: string;
  placeholder: string;
  onSave: (data: CustomFieldFormData) => Promise<boolean | null>;
  onClose: () => void;
  t: (key: string) => string;
};

async function runCustomFieldSave(params: RunCustomFieldSaveParams): Promise<void> {
  params.setPersistUi(s => ({ ...s, submitError: '' }));

  if (!params.validate()) return;

  const saveFailedMessage = params.t('web.admin.visitorManagement.customField.form.saveFailed');

  const setSaveError = (): void => {
    params.setPersistUi(s => ({ ...s, submitError: saveFailedMessage }));
  };

  params.setPersistUi(s => ({ ...s, saving: true }));

  try {
    const options = getOptionsForSave(params.fieldType, params.optionRows);
    const saved = await params.onSave({
      fieldName: params.fieldName.trim(),
      fieldKey: params.fieldKey.trim(),
      fieldType: params.fieldType,
      options,
      placeholder:
        isVisitorCustomFieldPlaceholderSupported(params.fieldType) && params.placeholder.trim()
          ? params.placeholder.trim()
          : undefined,
    });

    if (saved === true) {
      params.onClose();
    } else if (saved === false) {
      setSaveError();
    }
    // null: caller already showed a contextual toast — no additional error needed
  } catch {
    setSaveError();
  } finally {
    params.setPersistUi(s => ({ ...s, saving: false }));
  }
}

function reorderOptionRows(
  rows: CustomFieldOptionRow[],
  startIndex: number,
  endIndex: number
): CustomFieldOptionRow[] {
  const reordered = [...rows];
  const [removed] = reordered.splice(startIndex, 1);

  reordered.splice(endIndex, 0, removed);

  return reordered;
}

function validateFieldNameError(fieldName: string, fieldKey: string, t: TranslateFn): string {
  if (!fieldName.trim()) {
    return t('web.admin.visitorManagement.customField.form.validation.nameRequired');
  }

  if (fieldName.trim().length > CUSTOM_FIELD_LABEL_MAX_LENGTH) {
    return t('web.admin.visitorManagement.customField.form.validation.fieldLabelTooLong', { maxLength: CUSTOM_FIELD_LABEL_MAX_LENGTH });
  }

  if (!fieldKey.trim() || !/^[a-z][a-zA-Z0-9]*$/.test(fieldKey)) {
    return t('web.admin.visitorManagement.customField.form.validation.invalidFieldName');
  }

  return '';
}

function validateOptionsError(
  optionRows: CustomFieldOptionRow[],
  fieldTypeLabel: string,
  t: TranslateFn
): string {
  if (optionRows.length === 0) {
    return t('web.admin.visitorManagement.customField.form.validation.optionsRequired');
  }

  if (optionRows.length > CUSTOM_FIELD_SELECT_MAX_OPTIONS) {
    return t('web.admin.visitorManagement.customField.form.validation.maxOptionsReached', { fieldType: fieldTypeLabel, maxOptions: CUSTOM_FIELD_SELECT_MAX_OPTIONS });
  }

  if (optionRows.some(r => r.displayText.trim() === '' || r.value.trim() === '')) {
    return t('web.admin.visitorManagement.customField.form.validation.optionsBothRequired');
  }

  const displayTexts = optionRows.map(r => r.displayText.trim());

  if (new Set(displayTexts).size < displayTexts.length) {
    return t('web.admin.visitorManagement.customField.form.validation.optionsDuplicateDisplayText');
  }

  const values = optionRows.map(r => r.value.trim());

  if (new Set(values).size < values.length) {
    return t('web.admin.visitorManagement.customField.form.validation.optionsDuplicateValue');
  }

  return '';
}

function validateForm(params: ValidateFormParams): Record<string, string> {
  const { fieldName, fieldKey, fieldType, fieldTypeLabel, optionRows, placeholder, t } = params;
  const validationErrors: Record<string, string> = {};

  const fieldNameError = validateFieldNameError(fieldName, fieldKey, t);

  if (fieldNameError) validationErrors.fieldName = fieldNameError;

  if (placeholder.length > CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH) {
    validationErrors.placeholder = t('web.admin.visitorManagement.customField.form.validation.placeholderTooLong', { maxLength: CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH });
  }

  if (isVisitorCustomFieldSelectType(fieldType)) {
    const optionsError = validateOptionsError(optionRows, fieldTypeLabel, t);

    if (optionsError) validationErrors.options = optionsError;
  }

  return validationErrors;
}

function clearStaleOptionsError(
  setErrors: Dispatch<SetStateAction<Record<string, string>>>
): void {
  setErrors(prev => (prev.options ? { ...prev, options: '' } : prev));
}

function useCustomFieldForm({ onClose, onSave, t }: UseCustomFieldFormParams) {
  const [fieldName, setFieldName] = useState('');
  const [fieldKey, setFieldKey] = useState('');
  const [fieldType, setFieldType] = useState<VisitorCustomFieldType>(DEFAULT_FIELD_TYPE);
  const [optionRows, setOptionRows] = useState<CustomFieldOptionRow[]>([]);
  const [placeholder, setPlaceholder] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [persistUi, setPersistUi] = useState<SaveUiState>({ saving: false, submitError: '' });

  const handleNameChange = useCallback((value: string) => {
    const stripped = value.replace(/^\s+/, '');

    setFieldName(stripped);
    setFieldKey(deriveFieldKey(stripped));
    setErrors(prev => {
      if (stripped.length > CUSTOM_FIELD_LABEL_MAX_LENGTH) {
        const msg = t('web.admin.visitorManagement.customField.form.validation.fieldLabelTooLong', { maxLength: CUSTOM_FIELD_LABEL_MAX_LENGTH });

        return prev.fieldName === msg ? prev : { ...prev, fieldName: msg };
      }

      return prev.fieldName ? { ...prev, fieldName: '' } : prev;
    });
  }, [t]);

  const handleFieldTypeChange = useCallback((value: string) => {
    const newFieldType = coerceVisitorCustomFieldTypeFromUi(value);

    setFieldType(newFieldType);

    if (isVisitorCustomFieldSelectType(newFieldType)) {
      setOptionRows(prev => (prev.length === 0 ? createDefaultOptionRows() : prev));
      clearStaleOptionsError(setErrors);
    } else {
      setOptionRows([]);
      clearStaleOptionsError(setErrors);
    }

    if (!isVisitorCustomFieldPlaceholderSupported(newFieldType)) {
      setPlaceholder('');
      setErrors(prev => (prev.placeholder ? { ...prev, placeholder: '' } : prev));
    }
  }, []);

  const addOption = useCallback(() => {
    if (optionRows.length >= CUSTOM_FIELD_SELECT_MAX_OPTIONS) {
      const labelKey = getVisitorCustomFieldTypeLabelKey(fieldType);
      const fieldTypeLabel = (labelKey ? t(labelKey) : fieldType).toLowerCase();

      window.Toast.show(t('web.admin.visitorManagement.customField.form.validation.maxOptionsReached', { fieldType: fieldTypeLabel, maxOptions: CUSTOM_FIELD_SELECT_MAX_OPTIONS }));

      return;
    }

    setOptionRows(prev => [...prev, { id: uuid(), displayText: '', value: '' }]);
  }, [fieldType, optionRows.length, t]);

  const updateOption = useCallback((id: string, update: Partial<Pick<CustomFieldOptionRow, 'displayText' | 'value'>>) => {
    const isExceeded =
      (update.displayText !== undefined && update.displayText.length > CUSTOM_FIELD_SELECT_OPTION_TEXT_MAX_LENGTH) ||
      (update.value !== undefined && update.value.length > CUSTOM_FIELD_SELECT_OPTION_TEXT_MAX_LENGTH);

    const truncatedUpdate = {
      ...update,
      ...(update.displayText !== undefined && { displayText: update.displayText.slice(0, CUSTOM_FIELD_SELECT_OPTION_TEXT_MAX_LENGTH) }),
      ...(update.value !== undefined && { value: update.value.slice(0, CUSTOM_FIELD_SELECT_OPTION_TEXT_MAX_LENGTH) }),
    };

    if (isExceeded) {
      window.Toast.show(
        t('web.admin.visitorManagement.customField.form.validation.optionTextTooLong', { maxLength: CUSTOM_FIELD_SELECT_OPTION_TEXT_MAX_LENGTH })
      );
    }

    setOptionRows(prev =>
      prev.map(row => (row.id === id ? { ...row, ...truncatedUpdate } : row))
    );
    clearStaleOptionsError(setErrors);
  }, [t]);

  const removeOption = useCallback((id: string) => {
    setOptionRows(prev => prev.filter(row => row.id !== id));
    clearStaleOptionsError(setErrors);
  }, []);

  const onDragEnd = useCallback((result: DropResult) => {
    const destination = result.destination;

    if (!destination) return;

    setOptionRows(prev =>
      reorderOptionRows(prev, result.source.index, destination.index)
    );
  }, []);

  const handlePlaceholderChange = useCallback((value: string) => {
    setPlaceholder(value);
    setErrors(prev => {
      if (value.length > CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH) {
        const msg = t('web.admin.visitorManagement.customField.form.validation.placeholderTooLong', { maxLength: CUSTOM_FIELD_PLACEHOLDER_MAX_LENGTH });

        return prev.placeholder === msg ? prev : { ...prev, placeholder: msg };
      }

      return prev.placeholder ? { ...prev, placeholder: '' } : prev;
    });
  }, [t]);

  const validate = useCallback((): boolean => {
    const labelKey = getVisitorCustomFieldTypeLabelKey(fieldType);
    const fieldTypeLabel = (labelKey ? t(labelKey) : fieldType).toLowerCase();
    const validationErrors = validateForm({ fieldName, fieldKey, fieldType, fieldTypeLabel, optionRows, placeholder, t });

    setErrors(validationErrors);

    return Object.keys(validationErrors).length === 0;
  }, [fieldName, fieldKey, fieldType, optionRows, placeholder, t]);

  const handleSubmit = useCallback(
    () =>
      runCustomFieldSave({
        validate,
        setPersistUi,
        fieldType,
        optionRows,
        fieldName,
        fieldKey,
        placeholder,
        onSave,
        onClose,
        t,
      }),
    [
      fieldName,
      fieldKey,
      fieldType,
      optionRows,
      placeholder,
      t,
      validate,
      onSave,
      onClose,
    ]
  );

  const isSelectType = isVisitorCustomFieldSelectType(fieldType);
  const previewField = useMemo(() => {
    const previewOptions = isVisitorCustomFieldSelectType(fieldType)
      ? getOptionsForSave(fieldType, optionRows)
      : null;

    return {
      id: 'preview',
      organizationId: '',
      fieldKey: fieldKey || 'preview',
      fieldName: fieldName || t('web.admin.content.metatag.useMetatagEdit.newData'),
      fieldType,
      isEnabled: true,
      options: previewOptions,
      placeholder:
        isVisitorCustomFieldPlaceholderSupported(fieldType) && placeholder
          ? placeholder
          : undefined,
      createdAtDatetime: undefined,
      updatedAtDatetime: undefined,
      createdByUserId: '',
      updatedByUserId: '',
    };
  }, [fieldKey, fieldName, fieldType, optionRows, placeholder, t]);

  const isDirty = useMemo(
    () =>
      fieldName.trim().length > 0 ||
      placeholder.trim().length > 0 ||
      optionRows.length > 0 ||
      fieldType !== DEFAULT_FIELD_TYPE,
    [fieldName, fieldType, optionRows, placeholder]
  );

  return {
    fieldName,
    fieldKey,
    fieldType,
    optionRows,
    placeholder,
    errors,
    submitError: persistUi.submitError,
    saving: persistUi.saving,
    handleNameChange,
    handleFieldTypeChange,
    addOption,
    updateOption,
    removeOption,
    onDragEnd,
    handleSubmit,
    isSelectType,
    previewField,
    handlePlaceholderChange,
    onClose,
    t,
    isDirty,
  };
}

export function CustomFieldModal({
  onClose,
  onSave,
  onUnsavedDraftChange,
  saveDisabled,
}: CustomFieldModalProps): React.ReactElement {
  const { t } = useTranslation();

  const form = useCustomFieldForm({ onClose, onSave, t });
  const fieldTypeItems = useMemo(
    () => VISITOR_CUSTOM_FIELD_TYPE_DEFINITIONS.map(ft => ({ value: ft.value, label: t(ft.labelKey) })),
    [t]
  );

  const [previewValue, setPreviewValue] = useState<string | string[]>('');

  useEffect(() => {
    setPreviewValue('');
  }, [form.fieldType]);

  useEffect(() => {
    onUnsavedDraftChange?.(form.isDirty);
  }, [form.isDirty, onUnsavedDraftChange]);

  return (
    <div className={styles.modalRoot}>
      <div className={styles.entry}>
        <fieldset className={styles.field} data-test="input-type-dropdown">
          <Label h1>
            {form.t('web.admin.content.friendlyProperty.labels.inputType')}
          </Label>
          <Dropdown
            className={styles.fieldTypeDropdown}
            placeholder={form.t('web.admin.content.metatag.edit.fieldType.fieldTypeInputPlaceholder')}
            items={fieldTypeItems}
            value={form.fieldType}
            onValueChange={form.handleFieldTypeChange}
            doTranslation={false}
          />
        </fieldset>

        <fieldset className={styles.field} data-test="property-name">
          <Label h2 TooltipComponent={form.t('web.admin.visitorManagement.customField.form.name.tooltip')}>
            {form.t('web.admin.content.friendlyProperty.labels.name')}
            <span className={styles.required}>{REQUIRED_ASTERISK}</span>
          </Label>
          <Input
            value={form.fieldName}
            onChange={form.handleNameChange}
            placeholder={form.t('web.admin.content.friendlyProperty.placeholder.name')}
            aria-invalid={Boolean(form.errors.fieldName)}
          />
          {form.errors.fieldName && <span className={styles.fieldError}>{form.errors.fieldName}</span>}
        </fieldset>

        {form.isSelectType && (
          <fieldset className={styles.field} data-test="dropdown-options">
            <Label h2 TooltipComponent={form.t('web.admin.visitorManagement.customField.modal.options.description')}>
              {form.t('web.admin.content.friendlyProperty.labels.options')}
              <span className={styles.required}>{REQUIRED_ASTERISK}</span>
              <button
                type="button"
                className={styles.addOptionButton}
                onClick={form.addOption}
                aria-label={form.t('web.admin.visitorManagement.customField.form.options.addOption')}
                data-test="add-option-button"
              >
                <Icon
                  name="plus-circle"
                  set={ICON_SET_FONTAWESOME}
                  className={styles.addIcon}
                />
              </button>
            </Label>
            <CustomFieldModalOptionRowsEditor
              optionRows={form.optionRows}
              onUpdate={form.updateOption}
              onRemove={form.removeOption}
              onDragEnd={form.onDragEnd}
              optionsError={form.errors.options}
              t={form.t}
            />
          </fieldset>
        )}

        {isVisitorCustomFieldPlaceholderSupported(form.fieldType) && (
          <fieldset className={styles.field} data-test="custom-field-placeholder">
            <Label h2 TooltipComponent={form.t('web.admin.visitorManagement.customField.form.placeholder.tooltip')}>
              {form.t('web.admin.content.friendlyProperty.labels.placeholder')}
            </Label>
            <Input
              value={form.placeholder}
              onChange={form.handlePlaceholderChange}
              placeholder={form.t('web.admin.visitorManagement.customField.modal.placeholderPlaceholder')}
              aria-invalid={Boolean(form.errors.placeholder)}
            />
            {form.errors.placeholder && <span className={styles.fieldError}>{form.errors.placeholder}</span>}
          </fieldset>
        )}

        {form.submitError && (
          <span className={styles.fieldError} role="alert">
            {form.submitError}
          </span>
        )}
        <Flex gap={2} justify="end" className={styles.footerActions}>
          <Button variant="secondary" onClick={form.onClose} disabled={form.saving}>
            {form.t('web.admin.visitorManagement.customField.modal.cancel')}
          </Button>
          <Button variant="primary" onClick={form.handleSubmit} disabled={form.saving || Boolean(saveDisabled) || Object.values(form.errors).some(Boolean)} loading={form.saving}>
            {form.t('web.admin.visitorManagement.customField.modal.create')}
          </Button>
        </Flex>
      </div>

      <div className={styles.preview} data-test="preview-container">
        <fieldset className={styles.field}>
          <Label h1 TooltipComponent={form.t('web.admin.visitorManagement.customField.form.preview.tooltip')}>
            {form.t('web.admin.content.friendlyProperty.labels.preview')}
          </Label>
        </fieldset>
        <CustomFieldRenderer
          field={form.previewField}
          disabled={false}
          value={previewValue}
          onChange={setPreviewValue}
        />
      </div>
    </div>
  );
}
